// Uma ação de tela: manda um `Comando` pro Store e mostra o resultado.
//
// Antes, cada botão de painel repetia a mesma cadeia — limpar o erro, chamar
// o store, redesenhar no sucesso, e escrever `e.message` num `<p>` no erro.
// Eram 13 cópias, com 10 mensagens de fallback diferentes, e a mensagem
// aparecia duas vezes (o toast do store mais o texto inline).
//
// Aqui a cadeia vive uma vez só. O erro vai para o nó do painel e o store
// deixa de tostar (ver `aoErro` em `store.ts`), então a pessoa vê a mensagem
// uma vez, ao lado do campo que ela precisa corrigir.

import type { Comando } from "../../comandos/index.ts";
import type { Store } from "../store.ts";

/** Só o que a ação precisa do nó de erro — dá para testar sem DOM. */
export interface CampoErro {
  textContent: string | null;
}

/**
 * Executa o comando. `msgOk` vai no toast de sucesso (`null` = sem toast);
 * `aoOk` roda só quando deu certo, depois do redesenho do painel; `aoFalha`
 * roda só quando falhou, com a mesma mensagem amigável que vai pro campo de
 * erro — é o jeito de saber que falhou sem precisar checar `erro.textContent`
 * depois.
 *
 * A promessa devolvida **sempre resolve** — o erro já foi tratado e mostrado.
 * Ainda serve para `finally` (reabilitar um botão, por exemplo); mas para
 * saber se deu certo ou não, use `aoOk`/`aoFalha`, não o `then`/`catch`.
 */
export type AcaoComando = (
  comando: Comando,
  msgOk?: string | null,
  aoOk?: () => void,
  aoFalha?: (mensagem: string) => void,
) => Promise<void>;

/**
 * Liga uma ação ao painel: onde o erro aparece e o que redesenhar no sucesso.
 * Chame uma vez ao montar o painel e use o resultado em cada botão.
 */
export function criarAcaoComando(
  store: Store,
  erro: CampoErro,
  aoConcluir?: () => void,
): AcaoComando {
  return (comando, msgOk, aoOk, aoFalha) => {
    erro.textContent = "";
    return store
      .executar(comando, msgOk, (m) => {
        erro.textContent = m;
        aoFalha?.(m);
      })
      .then(() => {
        aoConcluir?.();
        aoOk?.();
      })
      .catch(() => {
        /* já mostrado em `erro` e repassado a `aoFalha` */
      });
  };
}

/**
 * Embrulha uma `AcaoComando` com uma mutação otimista: `aplicar()` roda antes
 * do comando ser disparado — antes até do Store ser chamado, e nunca dentro
 * de um callback — porque essa é a única forma de garantir que, se o Store
 * remontar a tela de forma síncrona (ele notifica os inscritos antes da
 * própria Promise de `executar` resolver), a tela remontada já veja o valor
 * novo. `desfazer()` roda só se o comando falhar, via `aoFalha` — sem
 * depender de sniff de `erro.textContent`.
 *
 * `aplicar()` estourando é tratado como falha (sem chamar `desfazer` — não
 * dá para saber se a mutação chegou a acontecer pela metade) em vez de
 * deixar a exceção escapar: a `AcaoComando` devolvida continua cumprindo a
 * promessa de sempre resolver, e um `btnFechar.disabled = true` já ligado
 * antes desta chamada não fica travado para sempre.
 */
export function comMutacaoOtimista(
  disparar: AcaoComando,
  aplicar: () => void,
  desfazer: () => void,
): AcaoComando {
  return (comando, msgOk, aoOk, aoFalha) => {
    try {
      aplicar();
    } catch (e) {
      const mensagem = e instanceof Error ? e.message : "Não foi possível concluir a operação.";
      aoFalha?.(mensagem);
      return Promise.resolve();
    }
    return disparar(comando, msgOk, aoOk, (mensagem) => {
      desfazer();
      aoFalha?.(mensagem);
    });
  };
}
